import { RepliersListingCondoDetails } from './repliers/RepliersListingCondoDetails';
import { instanceToPlain } from 'class-transformer';
import { FullListingRecord } from './FullListing';
import { RepliersDataCleaner } from '../utils/RepliersDataCleaner';
import isEmpty from '../helpers/isEmpty';
import '../jsTypeExtensions/numberAsPriceExtension';
import { RepliersListing } from './repliers/RepliersListing';
import { RepliersListingBuildingClass } from './repliers/RepliersListingBuildingClass';
import { RepliersListingCondoExposure } from './repliers/RepliersListingCondoExposure';
import sanitizeRepliersCondoExposure from '../helpers/repliersData/sanitizeRepliersCondoExposure';

const repliersFieldTransformations = {
  ammenities: 'amenities',
  buildingInsurance: 'hasBuildingInsurance',
  cableInlc: 'isCableIncluded',
  waterIncl: 'isWaterIncluded',
  heatIncl: 'isHeatIncluded',
  taxesIncl: 'isTaxesIncluded',
  parkingIncl: 'isParkingIncluded',
  hydroIncl: 'isHydroIncluded',
  ensuiteLaundry: 'hasEnsuiteLaundry',
  propertyMgr: 'propertyManager',
};

const typesForCleaning = {
  buildingInsurance: 'boolean',
  lockerNumber: 'number',
  cableInlc: 'boolean',
  waterIncl: 'boolean',
  heatIncl: 'boolean',
  taxesIncl: 'boolean',
  parkingIncl: 'boolean',
  hydroIncl: 'boolean',
  maintenance: 'number',
  lockerLevel: 'number',
  unitNumber: 'number',
  ensuiteLaundry: 'boolean',
  stories: 'number',
  condoCorpNum: 'number',
};

export type ListingCondoDetailsObject = {
  amenities: Array<null | string>;
  hasBuildingInsurance: null | boolean;
  condoCorp: null | string;
  condoCorpNum: null | number;
  exposure: null | RepliersListingCondoExposure;
  lockerNumber: null | number;
  lockerUnitNumber: null | string;
  locker: null | string;
  parkingType: null | string;
  pets: null | string;
  propertyManager: null | string;
  stories: null | number;
  fees: {
    isCableIncluded: boolean;
    isWaterIncluded: boolean;
    isHeatIncluded: boolean;
    isTaxesIncluded: boolean;
    isParkingIncluded: boolean;
    isHydroIncluded: boolean;
    maintenance: number;
  };
  lockerLevel: null | number;
  unitNumber: null | number;
  sharesPercentage: null | string;
  hasEnsuiteLaundry: null | boolean;
};

export type ListingCondoDetailsRecord = Omit<
  ListingCondoDetailsObject,
  'exposure'
>;

export class ListingCondoDetails {
  amenities: Array<null | string>;
  hasBuildingInsurance: null | boolean;
  condoCorp: null | string;
  condoCorpNum: null | number;
  exposure: null | RepliersListingCondoExposure;
  lockerNumber: null | number;
  lockerUnitNumber: null | string;
  locker: null | string;
  parkingType: null | string;
  pets: null | string;
  propertyManager: null | string;
  stories: null | number;
  fees: {
    isCableIncluded: boolean;
    isWaterIncluded: boolean;
    isHeatIncluded: boolean;
    isTaxesIncluded: boolean;
    isParkingIncluded: boolean;
    isHydroIncluded: boolean;
    maintenance: number;
  };
  lockerLevel: null | number;
  unitNumber: null | number;
  sharesPercentage: null | string;
  hasEnsuiteLaundry: null | boolean;

  constructor(listingCondoDetailsObject: ListingCondoDetailsObject) {
    this.amenities = listingCondoDetailsObject.amenities;
    this.hasBuildingInsurance = listingCondoDetailsObject.hasBuildingInsurance;
    this.condoCorp = listingCondoDetailsObject.condoCorp;
    this.condoCorpNum = listingCondoDetailsObject.condoCorpNum;
    this.exposure = listingCondoDetailsObject.exposure;
    this.lockerNumber = listingCondoDetailsObject.lockerNumber;
    this.lockerUnitNumber = listingCondoDetailsObject.lockerUnitNumber;
    this.locker = listingCondoDetailsObject.locker;
    this.parkingType = listingCondoDetailsObject.parkingType;
    this.pets = listingCondoDetailsObject.pets;
    this.propertyManager = listingCondoDetailsObject.propertyManager;
    this.stories = listingCondoDetailsObject.stories;
    this.fees = listingCondoDetailsObject.fees;
    this.lockerLevel = listingCondoDetailsObject.lockerLevel;
    this.unitNumber = listingCondoDetailsObject.unitNumber;
    this.sharesPercentage = listingCondoDetailsObject.sharesPercentage;
    this.hasEnsuiteLaundry = listingCondoDetailsObject.hasEnsuiteLaundry;
  }

  static fromRepliersListing(
    repliersListing: RepliersListing,
  ): null | ListingCondoDetails {
    if (repliersListing.class !== RepliersListingBuildingClass.Condo) {
      return null;
    }
    const cleaner = new RepliersDataCleaner<
      RepliersListingCondoDetails,
      ListingCondoDetailsObject
    >(repliersListing.condominium, {
      types: typesForCleaning,
      fieldTransformations: repliersFieldTransformations,
    });
    const condoDetailsObject = {
      ...cleaner.clean(),
      exposure: sanitizeRepliersCondoExposure(
        repliersListing.condominium.exposure,
      ),
    };
    return new ListingCondoDetails(condoDetailsObject);
  }

  static fromObject(
    listingCondoDetailsObject: ListingCondoDetailsObject,
  ): null | ListingCondoDetails {
    if (!listingCondoDetailsObject) {
      return null;
    }
    return new ListingCondoDetails(listingCondoDetailsObject);
  }

  static fromListingRecord(
    listingRecord: Partial<FullListingRecord>,
  ): null | ListingCondoDetails {
    if (!listingRecord.condo) {
      return null;
    }
    return new ListingCondoDetails({
      ...listingRecord.condo,
      exposure: listingRecord.condoExposure,
    });
  }

  maintenanceHeader(): string | null {
    if (isEmpty(this.fees.maintenance)) {
      return null;
    }
    return this.fees.maintenance.formatAsShortPrice() + '/Month';
  }

  includedMaintenanceFeesSentence(): string {
    const possibleServices = [
      'isCableIncluded',
      'isWaterIncluded',
      'isHeatIncluded',
      'isTaxesIncluded',
      'isParkingIncluded',
      'isHydroIncluded',
    ] as const;
    const FEE_TO_SERVICE_MAP: {
      [key in (typeof possibleServices)[number]]: string;
    } = {
      isCableIncluded: 'Cable',
      isWaterIncluded: 'Water',
      isHeatIncluded: 'Heat',
      isTaxesIncluded: 'Taxes',
      isParkingIncluded: 'Parking',
      isHydroIncluded: 'Hydro',
    };
    const includedServices = possibleServices
      .filter(service => Boolean(this.fees[service]))
      .map(service => FEE_TO_SERVICE_MAP[service]);
    return includedServices.join(', ') + ' included';
  }

  exposureDirection(): string | null {
    if (isEmpty(this.exposure)) {
      return null;
    }
    const exposure = this.exposure.toUpperCase();
    const EXPOSURE_MAP: { [key: string]: string } = {
      N: 'North',
      W: 'West',
      S: 'South',
      E: 'East',
      NE: 'Northeast',
      NW: 'Northwest',
      SE: 'Southeast',
      SW: 'Southwest',
      NS: 'Northsouth',
      EW: 'Eastwest',
    };
    return EXPOSURE_MAP[exposure] || exposure;
  }

  toObject(): ListingCondoDetailsObject {
    return instanceToPlain(this) as ListingCondoDetailsObject;
  }

  toListingCondoDetailsRecord(): ListingCondoDetailsRecord {
    const { exposure, ...listingCondoDetailsRecord } = this.toObject();
    return listingCondoDetailsRecord as ListingCondoDetailsRecord;
  }
}
